import React, { useState } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Tooltip,
  Badge,
  Spin,
  message,
  Popconfirm,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  useSchedulesForDateRange,
  useDeleteSchedule,
  Schedule,
  useStaff,
} from "../../hooks/useSchedule";
import ScheduleFilter, { FilterValues } from "./ScheduleFilter";
import ScheduleFormModal from "./ScheduleFormModal";
import ScheduleViewModal from "./ScheduleViewModal";
import {
  filterSchedules,
  getBadgeStatus,
  capitalize,
  getShiftColor,
} from "../../helpers/schedule";
import { useLocations } from "../../hooks/useUsers";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";

interface ScheduleGridViewProps {
  locationId?: number;
  canCreateSchedules: boolean;
  canModifySchedules: boolean;
  canDeleteSchedules: boolean;
}

// Helper function to get current user from localStorage
const getCurrentUser = () => {
  try {
    const userData =
      localStorage.getItem("user") || localStorage.getItem("currentUser");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting current user from localStorage:", error);
    return null;
  }
};

const ScheduleGridView: React.FC<ScheduleGridViewProps> = ({
  locationId,
  canCreateSchedules,
  canModifySchedules,
  canDeleteSchedules,
}) => {
  // Permission checks
  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);
  const canSeeAllSchedules = hasPermission(PERMISSIONS.CAN_SEE_ALL_SCHEDULES);

  const currentUser = getCurrentUser();

  // State
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  // Bulk delete state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filters state - Start from today, show next 30 days
  const [filters, setFilters] = useState<FilterValues>({
    location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
    date_range: [dayjs(), dayjs().add(30, "day")],
  });

  // Calculate date range from filters
  const startDate =
    filters.date_range?.[0]?.format("YYYY-MM-DD") ||
    dayjs().format("YYYY-MM-DD");
  const endDate =
    filters.date_range?.[1]?.format("YYYY-MM-DD") ||
    dayjs().add(30, "day").format("YYYY-MM-DD");

  // API Hooks - Convert UI filters to API filters
  const apiFilters = React.useMemo(() => {
    const processedFilters = { ...filters };

    // Remove user_ids from API call since API doesn't support it
    if ("user_ids" in processedFilters) {
      delete processedFilters.user_ids;
    }

    // Remove date_range from API call since API expects separate start_date/end_date parameters
    if ("date_range" in processedFilters) {
      delete processedFilters.date_range;
    }

    // Handle user filtering logic
    if (!canSeeAllSchedules && currentUser?.id) {
      // If user can't see all schedules, always use their own ID regardless of selection
      processedFilters.user_id = currentUser.id;
    } else if (filters.user_ids && filters.user_ids.length > 0) {
      // If user can see all schedules and has selected someone, use the first selected user
      processedFilters.user_id = filters.user_ids[0];
    }

    return processedFilters;
  }, [filters, canSeeAllSchedules, currentUser?.id]);

  const { data: schedulesData, isLoading: isLoadingSchedules } =
    useSchedulesForDateRange(startDate, endDate, apiFilters);
  const deleteScheduleMutation = useDeleteSchedule();

  // Get locations and staff
  const { data: locationsData, isLoading: isLoadingLocations } = useLocations();
  const { data: staffData, isLoading: isLoadingStaff } = useStaff(
    canSeeAllLocations ? undefined : currentUser?.location_id
  );

  // Apply filters to schedules and sort by date/time
  const filteredSchedules = React.useMemo(() => {
    const schedules = schedulesData?.data || [];
    const filtered = filterSchedules(schedules, filters);

    // Sort by date and time (earliest first)
    return filtered.sort((a: Schedule, b: Schedule) => {
      const dateA = dayjs(a.schedule_date + " " + a.start_time);
      const dateB = dayjs(b.schedule_date + " " + b.start_time);
      return dateA.valueOf() - dateB.valueOf();
    });
  }, [schedulesData, filters]);

  // Permission-aware handlers
  const handleViewSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setIsViewModalVisible(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    if (!canModifySchedules) {
      message.error("You don't have permission to edit schedules");
      return;
    }
    setSelectedSchedule(schedule);
    setIsViewModalVisible(false);
    setIsFormModalVisible(true);
  };

  const handleCreateSchedule = () => {
    if (!canCreateSchedules) {
      message.error("You don't have permission to create schedules");
      return;
    }
    setSelectedSchedule(null);
    setIsFormModalVisible(true);
  };

  const handleDeleteSchedule = (id: number) => {
    if (!canDeleteSchedules) {
      message.error("You don't have permission to delete schedules");
      return;
    }
    Modal.confirm({
      title: "Delete Schedule",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to delete this schedule? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteScheduleMutation.mutateAsync(id);
          setIsViewModalVisible(false);
        } catch (error) {
          console.error("Error deleting schedule:", error);
        }
      },
    });
  };

  const handleFilter = (values: FilterValues) => {
    setFilters(values);
    setIsFilterVisible(false);
  };

  const handleResetFilter = () => {
    setFilters({
      location_id: canSeeAllLocations ? locationId : currentUser?.location_id,
      date_range: [dayjs(), dayjs().add(30, "day")],
    });
    setIsFilterVisible(false);
  };

  // Bulk delete handlers
  const handleBulkDelete = async () => {
    if (!canDeleteSchedules) {
      message.error("You don't have permission to delete schedules");
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.warning("Please select schedules to delete");
      return;
    }

    // Limit bulk delete to 10 items
    if (selectedRowKeys.length > 10) {
      message.warning(
        "You can only delete up to 10 schedules at once. Please select fewer items."
      );
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} Schedule${
        selectedRowKeys.length > 1 ? "s" : ""
      }`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete ${
        selectedRowKeys.length
      } selected schedule${
        selectedRowKeys.length > 1 ? "s" : ""
      }? This action cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setIsBulkDeleting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
          // Delete each schedule individually since there's no bulk API
          for (const scheduleId of selectedRowKeys) {
            try {
              await deleteScheduleMutation.mutateAsync(Number(scheduleId));
              successCount++;
            } catch (error) {
              console.error(`Failed to delete schedule ${scheduleId}:`, error);
              errorCount++;
            }
          }

          // Show results
          if (successCount > 0) {
            message.success(
              `Successfully deleted ${successCount} schedule${
                successCount > 1 ? "s" : ""
              }`
            );
          }
          if (errorCount > 0) {
            message.error(
              `Failed to delete ${errorCount} schedule${
                errorCount > 1 ? "s" : ""
              }`
            );
          }

          // Clear selection
          setSelectedRowKeys([]);
        } finally {
          setIsBulkDeleting(false);
        }
      },
    });
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    // Limit selection to 10 items
    if (newSelectedRowKeys.length > 10) {
      message.warning(
        "You can only select up to 10 schedules for bulk operations"
      );
      return;
    }
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    onSelectAll: (
      selected: boolean,
      selectedRows: Schedule[],
      changeRows: Schedule[]
    ) => {
      if (selected) {
        // Select up to 10 visible schedules
        const allKeys = filteredSchedules
          .slice(0, 10)
          .map((schedule) => schedule.id);
        setSelectedRowKeys(allKeys);
        if (filteredSchedules.length > 10) {
          message.info(
            "Only the first 10 schedules were selected (bulk limit)"
          );
        }
      } else {
        // Deselect all
        setSelectedRowKeys([]);
      }
    },
  };

  // Define columns with permission-aware actions
  const getColumns = (): ColumnsType<Schedule> => {
    const baseColumns: ColumnsType<Schedule> = [
      {
        title: "Staff",
        dataIndex: ["user", "name"],
        key: "staff_name",
        sorter: (a: Schedule, b: Schedule) =>
          a.user.name.localeCompare(b.user.name),
      },
      {
        title: "Table",
        dataIndex: ["table", "number"],
        key: "number",
        render: (number: string) => <Tag color="green">{number}</Tag>,
      },
      // Only show location column if user can see all locations
      ...((canSeeAllLocations
        ? [
            {
              title: "Location",
              dataIndex: ["user", "location", "name"],
              key: "location",
              sorter: (a: Schedule, b: Schedule) =>
                a.user.location.name.localeCompare(b.user.location.name),
            },
          ]
        : []) as ColumnsType<Schedule>),
      {
        title: "Date",
        dataIndex: "schedule_date",
        key: "date",
        render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
        sorter: (a: Schedule, b: Schedule) =>
          dayjs(a.schedule_date).unix() - dayjs(b.schedule_date).unix(),
        defaultSortOrder: "ascend", // Default sort by date ascending
      },
      {
        title: "Shift",
        dataIndex: "shift_type",
        key: "shift_type",
        render: (shift: string) => (
          <Tag color={getShiftColor(shift)}>{shift.toUpperCase()}</Tag>
        ),
        filters: [
          { text: "Morning", value: "morning" },
          { text: "Day", value: "day" },
          { text: "Night", value: "night" },
        ],
        onFilter: (value, record) => record.shift_type === value,
      },
      {
        title: "Time",
        key: "time",
        render: (_, record: Schedule) => {
          const start = dayjs(record.start_time, "HH:mm:ss").format("h:mm A");
          const end = dayjs(record.end_time, "HH:mm:ss").format("h:mm A");
          return `${start} - ${end}`;
        },
        sorter: (a: Schedule, b: Schedule) => {
          const timeA = dayjs(a.start_time, "HH:mm:ss");
          const timeB = dayjs(b.start_time, "HH:mm:ss");
          return timeA.valueOf() - timeB.valueOf();
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
          <Badge status={getBadgeStatus(status)} text={capitalize(status)} />
        ),
        filters: [
          { text: "Scheduled", value: "scheduled" },
          { text: "Completed", value: "completed" },
          { text: "Canceled", value: "canceled" },
        ],
        onFilter: (value, record) => record.status === value,
      },
    ];

    // Only add actions column if user has any relevant permissions or view permission
    const actionColumn = {
      title: "Actions",
      key: "actions",
      render: (_: React.ReactNode, record: Schedule) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              type="text"
              onClick={() => handleViewSchedule(record)}
            />
          </Tooltip>

          {canModifySchedules && (
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                type="text"
                onClick={() => handleEditSchedule(record)}
              />
            </Tooltip>
          )}

          {canDeleteSchedules && (
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                type="text"
                danger
                onClick={() => handleDeleteSchedule(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    };

    baseColumns.push(actionColumn);
    return baseColumns;
  };

  const columns = getColumns();

  // Show total schedules count for debugging
  React.useEffect(() => {
    if (schedulesData) {
      console.log(
        `Grid: Loaded ${schedulesData.total} schedules for ${startDate} to ${endDate}, filtered to ${filteredSchedules.length}`,
        { canSeeAllSchedules, currentUser: currentUser?.id, apiFilters }
      );
    }
  }, [
    schedulesData,
    filteredSchedules,
    startDate,
    endDate,
    canSeeAllSchedules,
    currentUser,
    apiFilters,
  ]);

  // Prepare locations and staff data for filters
  const locations = canSeeAllLocations ? locationsData || [] : [];
  const staff = staffData?.data || [];

  if (isLoadingSchedules || isLoadingLocations || isLoadingStaff) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <Spin size="large" />
        <div className="mt-4 text-gray-600">
          {isLoadingSchedules &&
            `Loading schedules for ${dayjs(startDate).format(
              "MMM DD"
            )} - ${dayjs(endDate).format("MMM DD, YYYY")}...`}
          {isLoadingLocations && "Loading locations..."}
          {isLoadingStaff && "Loading staff..."}
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-grid">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {canSeeAllSchedules ? "Staff Schedule List" : "My Schedule List"}
        </h2>

        <Space>
          {selectedRowKeys.length > 0 && canDeleteSchedules && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} selected schedule${
                selectedRowKeys.length > 1 ? "s" : ""
              }?`}
              description="This action cannot be undone."
              onConfirm={handleBulkDelete}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
            >
              <Button danger loading={isBulkDeleting} icon={<DeleteOutlined />}>
                Delete {selectedRowKeys.length} Selected (max 10)
              </Button>
            </Popconfirm>
          )}

          <Button onClick={() => setIsFilterVisible(!isFilterVisible)}>
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>

          {canCreateSchedules && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateSchedule}
            >
              Add Schedule
            </Button>
          )}
        </Space>
      </div>

      {/* Filters */}
      {isFilterVisible && (
        <ScheduleFilter
          initialValues={filters}
          onFilter={handleFilter}
          onReset={handleResetFilter}
          locations={locations}
          staff={staff}
          canSeeAllSchedules={canSeeAllSchedules}
        />
      )}

      <Table
        columns={columns}
        dataSource={filteredSchedules}
        rowKey="id"
        rowSelection={canDeleteSchedules ? rowSelection : undefined}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["20", "50", "100", "200"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} schedules`,
        }}
        scroll={{ x: 800 }}
      />

      {/* View Schedule Modal - Using the new component */}
      <ScheduleViewModal
        visible={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        onEdit={handleEditSchedule}
        onDelete={handleDeleteSchedule}
        schedule={selectedSchedule}
        isDeleting={deleteScheduleMutation.isPending}
        canModifySchedules={canModifySchedules}
        canDeleteSchedules={canDeleteSchedules}
      />

      {/* Create/Edit Schedule Form Modal */}
      <ScheduleFormModal
        visible={isFormModalVisible}
        onCancel={() => setIsFormModalVisible(false)}
        onSubmit={() => setIsFormModalVisible(false)}
        schedule={selectedSchedule ?? undefined}
        locationFilter={filters.location_id}
      />
    </div>
  );
};

export default ScheduleGridView;
